<script setup lang="ts">
import {pagesPath} from '@/ts/router/pages-path';
import {
  PublicationState,
  SessionState,
  type Intervention, type Session,
} from '@/ts/types/dto/interventions-dto';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import balmBtn from '@/vue/atoms/balm-btn.vue';
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useRouter} from 'vue-router';
import InterventionLabel from '@/vue/molecules/intervention-label.vue';
import BadgeZeppelin from '@/vue/atoms/badge-zeppelin.vue';
import {getVueLink} from '@/ts/composables/stateful/use-route-params';
import {useProvidedIntervention} from '@/ts/composables/pure/use-provided-intervention';
import {useSession} from '@/ts/composables/pure/use-provided-session';
import {useUserStore} from '@/ts/store/user-store';

const {t: $t} = useI18n();
const api = useApi();
const router = useRouter();
const userStore = useUserStore();

const props = defineProps<{ intervention: Intervention }>();

const intervention = computed(() => {
  return props.intervention;
});

const {currentSession} = useProvidedIntervention(intervention);
const pageSession = computed<Session>(() => intervention.value.sessions.find(ses => currentSession.value?.id === ses.id)!);
const {lastPageIndexCompleted} = useSession(intervention, pageSession);

const currentSessionLink = computed<string | null>(() => {
  // Allow UNLOCK, or HOMEWORK_PENDING when cooldown has expired (homework is optional)
  const state = pageSession.value?.state;
  const hasCooldown = pageSession.value?.timeUntilUnlockMillis && pageSession.value.timeUntilUnlockMillis > 0;
  if (state !== SessionState.UNLOCK &&
      !(state === SessionState.HOMEWORK_PENDING && !hasCooldown)) {
    return null;
  }
  let index = 0;
  if (lastPageIndexCompleted.value !== null
      && lastPageIndexCompleted.value < pageSession.value?.pages.length) {
    index = lastPageIndexCompleted.value;
  }
  return  getVueLink(pagesPath.session.sessionPage, {
    iid: props.intervention.id,
    sid: currentSession.value!.id,
    pid: index + 1,
  });
});

const theme = computed(() => {
  if (props.intervention.publicationState === PublicationState.IN_PROGRESS) {
    return 'blue';
  }
  if (props.intervention.publicationState === PublicationState.COMPLETED) {
    return 'green';
  }
  return 'grey';
});

const enrollLabel = computed(() => (
  props.intervention.publicationState === PublicationState.WITHDREW ? $t('enroll_again') : $t('enroll')
));

const [enroll, enrollingInProgress] = useLoadingNotification(
  async() => {
    const updatedIntervention = await api.me.enrollIntervention(props.intervention.id);
    const updatedHomeContent = await api.me.getHomeContent();
    userStore.setHomeContent(updatedHomeContent);
    return updatedIntervention;
  },
  (updatedIntervention) => {
    // Calculate the correct link based on fresh intervention data
    const [firstSession] = updatedIntervention.sessions;
    const link = getVueLink(pagesPath.session.sessionPage, {
      iid: updatedIntervention.id,
      sid: firstSession.id,
      pid: 1,
    });
    void router.push(link);
  },
);

</script>

<template>
  <div class="intervention-hero" :class="theme">
    <div class="intervention-hero-back">
      <q-btn class="back-btn focus-visible" flat dense :to="pagesPath.intervention.interventionsList">
        <svg-illustration name="chevron-left.svg" aria-hidden="true"/>
        <span class="label">{{ $t('all_paths') }}</span>
      </q-btn>
      <intervention-label :publication-state="intervention.publicationState"/>
    </div>
    <div class="grid-container">
      <div class="image">
        <lazy-image :src="intervention.image!" :width="768" class="image-lazy"/>
      </div>
      <div class="info">
        <badge-zeppelin class="caption" :label="$t('path')"/>
        <h1 class="title">
          {{ intervention.name }}
        </h1>
        <balm-btn
          v-if="intervention.publicationState === PublicationState.IN_PROGRESS && currentSessionLink"
          :label="$t('continue')"
          :to="currentSessionLink!"
          theme="white"
          paddings="p3-7"
          icon="arrow-narrow-right.svg"
        />
        <template v-else-if="![PublicationState.COMPLETED, PublicationState.IN_PROGRESS].includes(intervention.publicationState)">
          <balm-btn
            paddings="p3-7"
            theme="white"
            :label="enrollLabel"
            :loading="enrollingInProgress"
            :disable="enrollingInProgress"
            @click="enroll"
          />
          <p v-if="intervention.publicationState === PublicationState.WITHDREW" class="withdraw-caption">
            {{ $t('previous_progress_will_not_be_restored') }}
          </p>
        </template>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.intervention-hero {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-radius: 12px;
  min-height: 300px;
  padding: var(--Spacing-spacing-6) var(--Spacing-spacing-7) var(--Spacing-spacing-6) var(--Spacing-spacing-6);
  background-color: var(--Light-Scheme-Functional-wl-current-surface);

  &.blue {
    background-color: var(--Light-Scheme-Functional-wl-current-surface);
  }
  &.green {
    background-color: var(--Light-Scheme-Secondary-secondary-color-2);
  }

  @include screen-md {
    min-height: 500px;
  }
}

.intervention-hero-back {
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-right: 4px;
}

.back-btn {
  color: var(--Functional-wl-current-text);
  border-radius: var(--Border-Radius-1);
  @include font-body-bold;
  margin-left: -12px; // offset q-btn padding + SVG viewbox whitespace to align chevron with card edge
}

.grid-container {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  gap: 12px;
  grid-template-areas:
    'image'
    'info';
  align-items: flex-end;

  @include screen-md {
    gap: 16px;
  }

  @include screen-lg {
    grid-template-columns: 1fr 2fr;
    grid-template-rows: 1fr;
    grid-template-areas: 'info image';
    gap: 24px;
  }
}

.label {
  color: var(--Light-Scheme-Functional-wl-current-text);
  padding-right: 4px;
}

.info {
  grid-area: info;
}

.image {
  grid-area: image;
  object-fit: contain !important;

  @include screen-md {
    max-height: 500px !important;
  }
  @include screen-lg {
    max-height: 600px !important;
  }

  & :deep(.q-img__image) {
    object-fit: contain !important;
  }
}

// .image-lazy img {
//   max-height: 140px !important;
// }

.caption {
  color: var(--Light-Scheme-Functional-wl-current-text);
  border: 0px;
  padding: 0px;
  margin-bottom: 4px;

  @include screen-md {
    margin-bottom: 8px;
  }

  @include screen-lg {
    margin-bottom: 12px;
  }
}

.title {
  color: var(--Light-Scheme-Functional-wl-current-text);
  margin-bottom: 16px;
  @include font-display-3;

  @include screen-md {
    margin-bottom: 24px;
  }

  @include screen-lg {
    margin-bottom: 32px;
  }
}

.withdraw-caption {
  // Match the rest of the hero (background, title, badge) which are light-pinned;
  // theme-aware here would invert the text in dark mode while the surface stays light.
  color: var(--Light-Scheme-Functional-wl-current-text);
  @include font-body-sm;
  margin-top: 12px;
  margin-bottom: 0;
  max-width: 420px;
}
</style>
